import { useEffect, useState } from 'react';
import { getFirebaseServices } from '@/lib/firebase/client';
import { doc, getDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

interface ReferralInfo {
  uid: string;
  handle: string;
  earnings: number;
  progress: number; // 0-100, based on $50 target
}

interface AffiliateStats {
  affiliateCode: string;
  referralLink: string;
  totalEarnings: number;
  referrals: ReferralInfo[];
  loading: boolean;
  canAddReferralCode: boolean;
  isLocked: boolean;
  referredByCode?: string;
  daysRemaining?: number;
}

/**
 * Hook to fetch and manage affiliate program stats
 */
export function useAffiliateStats(): AffiliateStats {
  const [stats, setStats] = useState<AffiliateStats>({
    affiliateCode: '',
    referralLink: '',
    totalEarnings: 0,
    referrals: [],
    loading: true,
    canAddReferralCode: false,
    isLocked: false,
  });

  useEffect(() => {
    let mounted = true;

    const loadStats = async () => {
      try {
        const services = getFirebaseServices();
        if (!services?.auth?.currentUser || !services?.db) {
          if (mounted) {
            setStats((prev) => ({ ...prev, loading: false }));
          }
          return;
        }

        const uid = services.auth.currentUser.uid;
        const userRef = doc(services.db, 'users', uid);
        const userSnap = await getDoc(userRef);

        if (!userSnap.exists() || !mounted) {
          if (mounted) setStats((prev) => ({ ...prev, loading: false }));
          return;
        }

        const userData = userSnap.data();
        console.log('User data loaded:', { uid, affiliateCode: userData.affiliateCode, referrals: userData.referrals });
        let affiliateCode = userData.affiliateCode || '';
        
        // Generate affiliate code if missing (for existing users)
        if (!affiliateCode && services.functions) {
          try {
            const { httpsCallable } = await import('firebase/functions');
            const generateCode = httpsCallable(services.functions, 'generateUniqueAffiliateCode');
            const result = await generateCode();
            affiliateCode = (result.data as any)?.code || '';
            
            // Update user document with new code
            if (affiliateCode) {
              const { updateDoc } = await import('firebase/firestore');
              await updateDoc(userRef, { 
                affiliateCode,
                affiliateEarnings: 0,
                referrals: []
              });
            }
          } catch (err) {
            console.error('Failed to generate affiliate code:', err);
          }
        }
        
        const referredBy = userData.referredBy;
        const referredByCode = userData.referredByCode;
        const createdAt = userData.createdAt?.toDate();
        const referralList = userData.referrals || [];
        const affiliateEarnings = userData.affiliateEarnings || 0;

        // Check if user can still add a referral code (within 7 days and not already linked)
        const daysSinceCreation = createdAt
          ? (Date.now() - createdAt.getTime()) / (1000 * 60 * 60 * 24)
          : 999;
        const canAddReferralCode = !referredBy && daysSinceCreation <= 7;
        const isLocked = !!referredBy;
        const daysRemaining = canAddReferralCode ? Math.max(0, Math.ceil(7 - daysSinceCreation)) : undefined;

        // Build referral link
        const referralLink = affiliateCode
          ? `${window.location.origin}?ref=${affiliateCode}`
          : '';

        // Fetch referral details securely via Cloud Function
        let referralInfoList: ReferralInfo[] = [];
        if (referralList.length > 0 && services.functions) {
          try {
            const getReferralStats = httpsCallable(services.functions, 'getReferralStats');
            const result = await getReferralStats();
            const data = result.data as any;
            
            if (data?.referrals) {
              referralInfoList = data.referrals.map((ref: any) => ({
                uid: ref.uid,
                handle: ref.handle,
                earnings: ref.totalEarned || 0,
                progress: Math.min((ref.totalEarned / 50) * 100, 100),
              }));
            }
          } catch (err) {
            console.error('Failed to fetch referral stats:', err);
          }
        }

        if (mounted) {
          setStats({
            affiliateCode,
            referralLink,
            totalEarnings: affiliateEarnings,
            referrals: referralInfoList,
            loading: false,
            canAddReferralCode,
            isLocked,
            referredByCode,
            daysRemaining,
          });
        }
      } catch (err) {
        console.error('Failed to load affiliate stats:', err);
        if (mounted) setStats((prev) => ({ ...prev, loading: false }));
      }
    };

    loadStats();

    return () => {
      mounted = false;
    };
  }, []);

  return stats;
}
